import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from './ui/command';

/**
 * Static index of every public page on the site.
 * Used as the search corpus.
 */
const buildSearchIndex = (t) => [
  // Aktiviteter
  {
    group: 'Aktiviteter',
    title: t('pg.SearchDialog.10066f'),
    description: t('pg.SearchDialog.6e670b'),
    keywords: 'soccer fotball A-lag akademi G6 G7 G8 G9 G10 G11 G12 trening kamper barn ungdom',
    path: '/soccer',
  },
  {
    group: 'Aktiviteter',
    title: t('pg.SearchDialog.0c493d'),
    description: t('pg.SearchDialog.ef4149'),
    keywords: 'tennis booking baner trening medlemskap utendørs',
    path: '/tennis',
  },
  {
    group: 'Aktiviteter',
    title: t('pg.SearchDialog.2ef8db'),
    description: t('pg.SearchDialog.f69398'),
    keywords: 'dans dance pilates yoga trening voksne barn ungdom',
    path: '/dance-pilates',
  },
  {
    group: 'Aktiviteter',
    title: t('pg.SearchDialog.736bff'),
    description: t('pg.SearchDialog.2c3b53'),
    keywords: 'social run club løping løpegruppe jogging mosjon barn ungdom voksen run',
    path: '/social-run-club',
  },
  {
    group: 'Aktiviteter',
    title: t('pg.SearchDialog.134399'),
    description: t('pg.SearchDialog.5c937c'),
    keywords: 'treningstid treningstider trening tider bane baneoversikt fotball schedule timeplan kalender',
    path: '/fotball/treningstider',
  },
  {
    group: 'Aktiviteter',
    title: t('pg.SearchDialog.443457'),
    description: t('pg.SearchDialog.e8725e'),
    keywords: 'kamper kampkalender fotball matcher resultater terminliste',
    path: '/kamper',
  },

  // Klubben
  {
    group: 'Klubben',
    title: t('pg.SearchDialog.214665'),
    description: t('pg.SearchDialog.d2bcd1'),
    keywords: 'hjem home forside hovedside',
    path: '/',
  },
  {
    group: 'Klubben',
    title: t('pg.SearchDialog.c809e2'),
    description: t('pg.SearchDialog.1f8ba4'),
    keywords: 'om oss about historie 1955 verdier veitvet bjerke oslo',
    path: '/about',
  },
  {
    group: 'Klubben',
    title: t('pg.SearchDialog.0b55a8'),
    description: t('pg.SearchDialog.198072'),
    keywords: 'styret board ledelse leder nestleder styremedlem varamedlem',
    path: '/styret',
  },
  {
    group: 'Klubben',
    title: t('pg.SearchDialog.92316d'),
    description: t('pg.SearchDialog.bd11af'),
    keywords: 'kontakt contact telefon epost adresse beliggenhet',
    path: '/contact',
  },
  {
    group: 'Klubben',
    title: t('pg.SearchDialog.a7260e'),
    description: t('pg.SearchDialog.28582b'),
    keywords: 'priser pricing prix tarifs precios cuotas qiimaha أسعار رسوم medlemsavgift treningsavgift kontingent kostnad fotball tennis dans pilates run club',
    path: '/pricing',
  },
  {
    group: 'Klubben',
    title: t('pg.SearchDialog.6aba99'),
    description: t('pg.SearchDialog.a0122c'),
    keywords: 'sponsor sponsorer samarbeidspartner partnerskap næringsliv bedrift bli sponsor',
    path: '/bli-sponsor',
  },
  {
    group: 'Klubben',
    title: t('pg.SearchDialog.274ecf'),
    description: t('pg.SearchDialog.f7c63e'),
    keywords: 'utleie rentals lokale bane leie hall klubbhus',
    path: '/rentals',
  },

  // Informasjon
  {
    group: 'Informasjon',
    title: t('pg.SearchDialog.73170d'),
    description: t('pg.SearchDialog.a60086'),
    keywords: 'nyheter news arkiv aktuelt',
    path: '/nyheter',
  },
  {
    group: 'Informasjon',
    title: t('pg.SearchDialog.c4be9d'),
    description: t('pg.SearchDialog.3950d9'),
    keywords: 'fritidskort fritidskortet friskus hoopit støtte oslo kommune betaling faktura',
    path: '/fritidskortet',
  },
  {
    group: 'Informasjon',
    title: t('pg.SearchDialog.560a1b'),
    description: t('pg.SearchDialog.2bd1a8'),
    keywords: 'politiattest politi attest trener frivillig sikkerhet barn',
    path: '/politiattest',
  },
  {
    group: 'Informasjon',
    title: t('pg.SearchDialog.f871d7'),
    description: t('pg.SearchDialog.632fe3'),
    keywords: 'årsmøte årsmote protokoll referat budsjett regnskap saksliste',
    path: '/aarsmoeteprotokoll',
  },
  {
    group: 'Informasjon',
    title: t('pg.SearchDialog.b535d1'),
    description: t('pg.SearchDialog.b97a8b'),
    keywords: 'strategi sportsplan plan utvikling 2026 2031 strategiplan',
    path: '/strategi-sportsplan',
  },
  {
    group: 'Informasjon',
    title: t('pg.SearchDialog.e5e209'),
    description: t('pg.SearchDialog.25acf5'),
    keywords: 'personvern privacy GDPR cookies',
    path: '/personvern',
  },

  // Butikk
  {
    group: 'Butikk',
    title: t('pg.SearchDialog.48b030'),
    description: t('pg.SearchDialog.3ffe22'),
    keywords: 'butikk store shop klubbkolleksjon klubbstolthet drakt cometsport',
    path: '/store',
  },
];

const matchesQuery = (item, query) => {
  if (!query) return true;
  const q = query.toLowerCase();
  return (
    item.title.toLowerCase().includes(q) ||
    item.description.toLowerCase().includes(q) ||
    (item.keywords || '').toLowerCase().includes(q)
  );
};

export const SearchDialog = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const index = useMemo(() => buildSearchIndex(t).map((e) => ({ ...e, keywords: `${e.keywords} ${e.title} ${e.description}`.toLowerCase() })), [t]);
  const groupLabel = { Aktiviteter: t('search.groups.activities'), Klubben: t('search.groups.club'), Informasjon: t('search.groups.info'), Butikk: t('search.groups.store') };

  // Cmd+K / Ctrl+K shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onOpenChange]);

  // Reset query when dialog closes
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);


  const grouped = useMemo(() => {
    const filtered = index.filter((item) => matchesQuery(item, query));
    return filtered.reduce((acc, item) => {
      if (!acc[item.group]) acc[item.group] = [];
      acc[item.group].push(item);
      return acc;
    }, {});
  }, [index, query]);

  const handleSelect = (path) => {
    onOpenChange(false);
    navigate(path);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder={t('search.placeholder')}
        value={query}
        onValueChange={setQuery}
        data-testid="search-input"
      />
      <CommandList>
        <CommandEmpty>{t('search.noResults')}</CommandEmpty>
        {Object.entries(grouped).map(([group, items]) => (
          <CommandGroup key={group} heading={groupLabel[group] || group}>
            {items.map((item) => (
              <CommandItem
                key={item.path}
                value={`${item.title} ${item.description} ${item.keywords}`}
                onSelect={() => handleSelect(item.path)}
                data-testid={`search-result-${item.path.replace(/\//g, '-') || 'home'}`}
                className="cursor-pointer"
              >
                <Search className="mr-2 h-4 w-4 text-green-500" />
                <div className="flex flex-col">
                  <span className="font-medium text-slate-900 dark:text-slate-100">
                    {item.title}
                  </span>
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    {item.description}
                  </span>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
    </CommandDialog>
  );
};

export default SearchDialog;
